/**
 * Penalty mode: level layouts (same objects as SpeedRun + ball spawn).
 * Official levels load from `/api/penalty-levels`.
 */
export type {
    NormSeg,
    WallSegDef,
    MovingLaserDef,
    SpeedrunToggleGroup,
    SpeedrunPointType,
    OscPathMove,
    OscPointMove,
    SpeedrunWindDir,
    SpeedrunPortalDef,
    SpeedrunPortalSide,
    SpeedrunPointDef,
    SpeedrunGoalDef,
    SpeedrunCheckpointDef,
    SpeedrunPlaytestCheckpointDef,
    SpeedrunWindDef,
    SpeedrunTextDef,
    SpeedrunLevelDef,
} from "./speedrun-levels";

export {
    normalizeLevelData,
    distPointToSeg,
    sweptCircleHitsCircle,
    distSegToSeg2D,
    circleRectOverlap,
    wallSegToPx,
    wallDrawAlpha,
    speedrunGroupRgb,
    movingLaserSegNorm,
    speedrunPointGoalPosNorm,
    speedrunPointTypeOf,
    speedrunTogglePointTypeOf,
    drawSpeedrunPointShape,
    drawSpeedrunCheckpointDeltoid,
    speedrunCheckpointRadiusPx,
    speedrunLevelCheckpoints,
    speedrunLevelTemporalSpawn,
    speedrunLaserHalfPxForWall,
    speedrunLaserHalfPxForMoving,
    wallSolidAnimatesAtRuntime,
    normPointInWindZone,
    normPointInTextZone,
    speedrunWindDirUnit,
    speedrunWindStrengthValue,
    drawSpeedrunTextLabel,
    speedrunTextRotateHandlePx,
    speedrunRotateWheelHandlePx,
    speedrunPortalColor,
    speedrunPortalHalfThicknessPx,
    speedrunPortalPairmate,
    speedrunPortalClosestPx,
    speedrunPortalMapPointPx,
    speedrunPortalTryMotionCrossPx,
    drawSpeedrunPortalLine,
    SPEEDRUN_LASER_HALF_BASE_PX,
    SPEEDRUN_TOGGLE_GROUP_IDS,
    SPEEDRUN_POINT_TYPES,
    SPEEDRUN_PORTAL_GROUP_COLORS,
} from "./speedrun-levels";

import { normalizeLevelData, type SpeedrunLevelDef } from "./speedrun-levels";

export type PenaltyLevelDef = SpeedrunLevelDef & {
    /** Normalized ball start (0–1). */
    ballSpawn: { nx: number; ny: number };
};

export type PenaltyLevelPayload = {
    levelIndex: number;
    data: unknown;
    displayName?: string | null;
    description?: string | null;
    authorName?: string | null;
    viewCount?: number;
};

export type PenaltyLevelCardMeta = {
    displayName?: string | null;
    description?: string | null;
    authorName?: string | null;
    viewCount: number;
};

const runtimeLevelMeta = new Map<number, PenaltyLevelCardMeta>();
const runtimeLevels = new Map<number, PenaltyLevelDef>();

let playtestOverride: PenaltyLevelDef | null = null;
let playtestLevelIndex = 1;

export function setRuntimePenaltyLevelMetaFromPayload(
    levels: Array<{
        levelIndex: number;
        displayName?: string | null;
        description?: string | null;
        authorName?: string | null;
        viewCount?: number;
    }>,
) {
    runtimeLevelMeta.clear();
    for (const row of levels) {
        const idx = Math.floor(Number(row.levelIndex));
        if (!Number.isInteger(idx) || idx < 1) continue;
        runtimeLevelMeta.set(idx, {
            displayName: row.displayName ?? null,
            description: row.description ?? null,
            authorName: row.authorName ?? null,
            viewCount: Math.max(0, Math.floor(Number(row.viewCount) || 0)),
        });
    }
}

export function getPenaltyLevelCardMeta(level: number): PenaltyLevelCardMeta | null {
    return runtimeLevelMeta.get(Math.floor(level)) ?? null;
}

export function getPenaltyLevelDisplayTitle(level: number): string {
    const meta = getPenaltyLevelCardMeta(level);
    const name = meta?.displayName?.trim();
    return name || `Level ${Math.floor(level)}`;
}

export function bumpPenaltyLevelViewCountLocal(level: number, viewCount: number): void {
    const idx = Math.floor(level);
    const prev = runtimeLevelMeta.get(idx);
    if (prev) runtimeLevelMeta.set(idx, { ...prev, viewCount: Math.max(0, Math.floor(viewCount)) });
}

export function setRuntimePenaltyLevelsFromPayload(levels: PenaltyLevelPayload[]) {
    runtimeLevels.clear();
    for (const row of levels) {
        const idx = Math.floor(Number(row.levelIndex));
        if (!Number.isInteger(idx) || idx < 1) continue;
        if (!penaltyPayloadLooksValid(row.data)) continue;
        runtimeLevels.set(idx, normalizePenaltyLevelData(row.data));
    }
    setRuntimePenaltyLevelMetaFromPayload(levels);
}

function clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
}

const PENALTY_STUB: PenaltyLevelDef = {
    spawn: { nx: 0.5, ny: 0.92 },
    goal: { nx: 0.5, ny: 0.08, nHalf: 0.035 },
    staticSegs: [],
    moving: [],
    points: [],
    ballSpawn: { nx: 0.5, ny: 0.55 },
};

function unwrapPenaltyPayload(raw: unknown): unknown {
    if (typeof raw === "string") {
        try {
            return unwrapPenaltyPayload(JSON.parse(raw));
        } catch {
            return null;
        }
    }
    if (!raw || typeof raw !== "object") return raw;
    const o = raw as Record<string, unknown>;
    const nested = o.data;
    const hasTop =
        o.spawn != null ||
        o.ballSpawn != null ||
        o.goal != null ||
        o.staticSegs != null ||
        o.moving != null;
    if (!hasTop && nested != null && typeof nested === "object") {
        return unwrapPenaltyPayload(nested);
    }
    return raw;
}

export function penaltyPayloadLooksValid(raw: unknown): boolean {
    const unwrapped = unwrapPenaltyPayload(raw);
    if (!unwrapped || typeof unwrapped !== "object") return false;
    const o = unwrapped as Record<string, unknown>;
    const spawn = o.spawn;
    const goal = o.goal;
    if (!spawn || typeof spawn !== "object" || !goal || typeof goal !== "object") return false;
    const sn = spawn as Record<string, unknown>;
    const gn = goal as Record<string, unknown>;
    return (
        Number.isFinite(Number(sn.nx)) &&
        Number.isFinite(Number(sn.ny)) &&
        Number.isFinite(Number(gn.nx)) &&
        Number.isFinite(Number(gn.ny))
    );
}

/** Normalize JSON from DB or editor into a runtime def. */
export function normalizePenaltyLevelData(raw: unknown): PenaltyLevelDef {
    const base = normalizeLevelData(raw);
    const unwrapped = unwrapPenaltyPayload(raw);
    const o = unwrapped && typeof unwrapped === "object" ? (unwrapped as Record<string, unknown>) : {};
    const bsRaw = o.ballSpawn;
    let ballSpawn = PENALTY_STUB.ballSpawn;
    if (bsRaw && typeof bsRaw === "object") {
        const b = bsRaw as Record<string, unknown>;
        const nx = Number(b.nx);
        const ny = Number(b.ny);
        if (Number.isFinite(nx) && Number.isFinite(ny)) {
            ballSpawn = { nx: clamp(nx, 0, 1), ny: clamp(ny, 0, 1) };
        }
    }
    return { ...base, ballSpawn };
}

export function getPenaltyPublishedLevelIndices(): number[] {
    return [...runtimeLevels.keys()].sort((a, b) => a - b);
}

export function hasPenaltyPublishedLevel(level: number): boolean {
    const lv = Math.floor(level);
    return Number.isInteger(lv) && lv >= 1 && runtimeLevels.has(lv);
}

export function getNextPenaltyPublishedLevel(afterLevel: number): number | null {
    const lv = Math.floor(afterLevel);
    for (const idx of getPenaltyPublishedLevelIndices()) {
        if (idx > lv) return idx;
    }
    return null;
}

export function getPenaltyLevelCount(): number {
    if (runtimeLevels.size === 0) return 0;
    return Math.max(...runtimeLevels.keys());
}

export function getPenaltyLevelDef(level: number): PenaltyLevelDef {
    const lv = Math.floor(level);
    if (playtestOverride && lv === playtestLevelIndex) return playtestOverride;
    const d = runtimeLevels.get(lv);
    if (d) return d;
    return PENALTY_STUB;
}

export function setPenaltyPlaytestOverride(levelIndex: number, def: PenaltyLevelDef | null) {
    playtestLevelIndex = Math.max(1, Math.floor(levelIndex));
    playtestOverride = def;
}

export function clearPenaltyPlaytestOverride() {
    playtestOverride = null;
}
